//  Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, OnInit } from '@angular/core';
import {HeaderService} from '../../header.service';
import {TranslateService} from '@ngx-translate/core';
import {EncryptionEventService} from '../../../services/encryption-event.service';
import {SelectedOrgMethod} from '../../../shared/selectedOrg';
import {IColumnChooserElement} from '../../shared/components/column-chooser/column-chooser.component';
import {EncryptionEventDataSource} from './encryption-event-data-source';
import {
    ConsoleEncryptionEventNames, ConsoleEncryptionScopeDisplay, ConsoleEncryptionScopeNames,
    getConsoleEncryptionEventNames,
    getConsoleEncryptionScopeNames
} from '../../../types/encryption-events';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { D3eService } from '../../../services/d3e-service';
import { ConfigurationService } from '../../../services/configuration-service';
import { catchError } from 'rxjs/operators';
import { IThreatCsvToken } from '../../../types/threat';
import { of } from 'rxjs';
import multiDownload from 'multi-download';
import { UntypedFormControl } from '@angular/forms';
import { SortAttr } from '../../../types/common';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-encryption-event-viewer',
    templateUrl: './encryption-event-viewer.component.html',
    styleUrls: ['./encryption-event-viewer.component.scss'],
    standalone: false
})
export class EncryptionEventViewerComponent implements OnInit {

    columns: IColumnChooserElement[] = [];
    columnsToDisplay: string[] = [];
    public dataSource!: EncryptionEventDataSource;
    searchTerm = '';
    lastScrollTop = 0;
    eventTypeForm = new UntypedFormControl();
    eventTypes: {"index": string, "value": string}[] = [];
    selectedEventTypes: {"index": string, "value": string}[] = [];
    scopeForm = new UntypedFormControl();
    scopeTypes: {"index": string, "value": ConsoleEncryptionScopeDisplay}[] = [];
    selectedScopes: {"index": string, "value": ConsoleEncryptionScopeDisplay}[] = [];

    eventNames!: ConsoleEncryptionEventNames;
    scopeNames!: ConsoleEncryptionScopeNames;

    selectedOrgId: string = null;

    queryPageStart = 0;
    queryPageSize = 50;
    sortAttrs: [SortAttr] = [{ 'sortAttr': 'dttm', 'sortOrder': 'desc' }];

    constructor(private readonly headerService: HeaderService,
                private readonly translate: TranslateService,
                private readonly encryptionEventsService: EncryptionEventService,
                private readonly d3eService: D3eService,
                private readonly configurationService: ConfigurationService) {

        setTimeout(() => {
            this.headerService.showUserFilter(true);
            this.headerService.setOrgSelectorDefaultOption(translate.instant('encryption-events-viewer.default-org-selector-text'));
        }, 0)

        this.eventNames = getConsoleEncryptionEventNames(translate);
        this.scopeNames = getConsoleEncryptionScopeNames(translate);

        this.columns = [
            {title: translate.instant('encryption-events-viewer.columns.dttm'), value: 'dttm', selected: true, hidden: false},
            {title: translate.instant('encryption-events-viewer.columns.client-dttm'), value: 'clientDttm', selected: true, hidden: false},
            {title: translate.instant('encryption-events-viewer.columns.email'), value: 'email', selected: true, hidden: false},
            {title: translate.instant('encryption-events-viewer.columns.computer-name'), value: 'computerName', selected: true, hidden: false},
            {title: translate.instant('encryption-events-viewer.columns.key-name'), value: 'keyName', selected: true, hidden: false},
            {title: translate.instant('encryption-events-viewer.columns.key-scope'), value: 'keyScope', selected: false, hidden: false},
            {title: translate.instant('encryption-events-viewer.columns.client-id'), value: 'clientId', selected: true, hidden: false},
            {title: translate.instant('encryption-events-viewer.columns.event-type'), value: 'eventType', selected: true, hidden: false},
            {title: translate.instant('encryption-events-viewer.columns.consumer'), value: 'consumer', selected: true, hidden: false},
            {title: translate.instant('encryption-events-viewer.columns.user-name'), value: 'userName', selected: true, hidden: false},
            {title: translate.instant('encryption-events-viewer.columns.user-uid'), value: 'userUid', selected: true, hidden: false},
            {title: translate.instant('encryption-events-viewer.columns.file-name'), value: 'fileName', selected: true, hidden: false},
            {title: translate.instant('encryption-events-viewer.columns.folder-name'), value: 'folderName', selected: true, hidden: false},
            {title: translate.instant('encryption-events-viewer.columns.data-id'), value: 'dataId', selected: true, hidden: false},
        ];
    }

    ngOnInit(): void {
        this.dataSource = new EncryptionEventDataSource(this.encryptionEventsService, 50);
        this.headerService.orgSelectedChanged
            .pipe(untilDestroyed(this))
            .subscribe(selectedOrgId => {
                selectedOrgId = SelectedOrgMethod.getSelectedOrg(selectedOrgId);
                this.selectedOrgId = selectedOrgId;
                this.refreshEventsViewer();
        });

        for (const [index, value] of Object.entries(this.eventNames)) {
            this.eventTypes.push({"index": index, "value": value});
        }
        for (const [index, value] of Object.entries(this.scopeNames)) {
            this.scopeTypes.push({"index": index, "value": value});
        }

    }

    updateDisplayedColumns(columns: string[]) {
        this.columnsToDisplay = columns;
    }

    tableScroll(event) {
        const scrollTop = event.target.scrollTop;
        if (this.lastScrollTop !== scrollTop) {
            this.lastScrollTop = scrollTop;
            const tableViewHeight = event.target.offsetHeight; // viewport: ~500px
            const tableScrollHeight = event.target.scrollHeight; // length of all table
            const scrollLocation = event.target.scrollTop; // how far user scrolled

            const buffer = 600;
            // Take absolute value to stop the horizontal scrollbar from triggering new results on short table (height).
            const limit = tableScrollHeight - tableViewHeight - buffer;
            if (scrollLocation > limit && !this.dataSource.loading && this.dataSource.haveMoreData()) {
                const selectedEventTypes = this.selectedEventTypes.map(x => x.index);
                const selectedScopes = this.selectedScopes.map(x => x.value.value);
                this.dataSource.extend(this.selectedOrgId, this.searchTerm, selectedEventTypes, selectedScopes, this.sortAttrs);
            }
        }
    }

    filterSubscriptions() {
        this.refreshEventsViewer();
    }

    clearSearchTerm() {
        this.searchTerm = '';
        this.refreshEventsViewer();
    }

    refreshEventsViewer() {
        this.lastScrollTop = 0;
        const selectedEventTypes = this.selectedEventTypes.map(x => x.index);
        const selectedScopes = this.selectedScopes.map(x => x.value.value);
        this.dataSource.fetch(this.selectedOrgId, this.searchTerm, selectedEventTypes, selectedScopes, this.sortAttrs);
    }

    buildQuery() {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
        // We need to send back just the enum values to the API, which is the index here.
        const selectedEventTypes = this.selectedEventTypes.map(x => x.index);
        const selectedScopes = this.selectedScopes.map(x => x.value.value);

        return {
          'pageStart': this.queryPageStart,
          'pageSize': this.queryPageSize,
          'event_types': selectedEventTypes,
          'scopes': selectedScopes,
          'sorts': this.sortAttrs,
          'search': this.searchTerm,
          'selectedOrgId': selectedOrg,
          'columnsToDisplay': null,
          'translations': null
        };
    }

    onEventTypeChange() {
        this.selectedEventTypes = [];
        const selectedEventTypes = [];
        if (this.eventTypeForm.value !== undefined && this.eventTypeForm.value.length > 0) {
            this.eventTypeForm.value.forEach(e => {
                selectedEventTypes.push(e)
            });

            for (const [index, value] of Object.entries(this.eventNames)) {
                if (selectedEventTypes.includes(value)) {
                    this.selectedEventTypes.push({"index": index, "value": value});
                }
            }

        }
        this.refreshEventsViewer();
    }

    onScopeChange() {
        this.selectedScopes = [];
        const selectedScopes = [];
        if (this.scopeForm.value !== undefined && this.scopeForm.value.length > 0) {
            this.scopeForm.value.forEach(e => {
                selectedScopes.push(e)
            });

            for (const [index, value] of Object.entries(this.scopeNames)) {
                if (selectedScopes.includes(value)) {
                    this.selectedScopes.push({index: index, value: value});
                }
            }
        }
        this.refreshEventsViewer();
    }

    exportCSV() {
        const query = this.buildQuery();
        query.pageSize = 0;
        query.columnsToDisplay = this.columnsToDisplay;
        this.d3eService.get_threats_csv_token(query)
          .pipe(
              catchError(() => of({} as IThreatCsvToken)),
              untilDestroyed(this))
          .subscribe(response => {
            const url = this.configurationService.apiUrl + '/api/v1/encryption-events/csv?q=' + response.token;
            multiDownload([url], {
              // tslint:disable-next-line:no-shadowed-variable
              rename: (url, index, urls) => 'encryption-events.csv',
            });
          });
    }

    exportJSON() {
        const query = this.buildQuery();
        query.pageSize = 0;
        query.columnsToDisplay = this.columnsToDisplay;
        this.d3eService.get_threats_csv_token(query)
          .pipe(
              catchError(() => of({} as IThreatCsvToken)),
              untilDestroyed(this))
          .subscribe(response => {
            const url = this.configurationService.apiUrl + '/api/v1/encryption-events/json?q=' + response.token;
            multiDownload([url], {
              // tslint:disable-next-line:no-shadowed-variable
              rename: (url, index, urls) => 'encryption-events.json',
            });
          });
    }

    sortColumn(event) {
        let attr = event.active;
        let direction = event.direction;
        if (direction == '') {
            direction = 'desc';
        }

        const sort = {
          'sortAttr': attr,
          'sortOrder': direction
        };

        this.sortAttrs = [ sort ];

        // changing sorting changes page start
        this.queryPageStart = 0;

        this.refreshEventsViewer();

    }
}
